/**
 * HBA API client, shared by /app, /microsite and /scanner.
 *
 * The entity surface deliberately mirrors the Base44 SDK the mockup was built
 * against (`entities.Session.list()`, `.filter()`, `.create()`, ...) so the
 * ported pages keep working without rewriting every call site.
 */

const DEFAULT_BASE = '/api/v1';

/** Thrown for any non-2xx response. `status` and `code` drive UI branching. */
export class ApiError extends Error {
  constructor(message, { status, code, details } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  get isAuthError() {
    return this.status === 401 || this.status === 403;
  }

  /** True when the server rejected a booking because someone got there first. */
  get isConflict() {
    return this.status === 409;
  }
}

/**
 * Token storage is injected rather than assumed: the web builds use
 * localStorage, the Capacitor app uses Preferences (async, native-backed).
 */
const memoryStore = new Map();
export const memoryTokenStore = {
  get: (k) => memoryStore.get(k) ?? null,
  set: (k, v) => void memoryStore.set(k, v),
  remove: (k) => void memoryStore.delete(k),
};

export const localStorageTokenStore = {
  get(k) {
    try {
      return globalThis.localStorage?.getItem(k) ?? null;
    } catch {
      return memoryTokenStore.get(k);
    }
  },
  set(k, v) {
    try {
      globalThis.localStorage?.setItem(k, v);
    } catch {
      memoryTokenStore.set(k, v);
    }
  },
  remove(k) {
    try {
      globalThis.localStorage?.removeItem(k);
    } catch {
      memoryTokenStore.remove(k);
    }
  },
};

const ACCESS_KEY = 'hba.access_token';
const REFRESH_KEY = 'hba.refresh_token';

export function createClient({
  baseUrl = DEFAULT_BASE,
  tokenStore = localStorageTokenStore,
  getLocale = () => globalThis.document?.documentElement?.lang || 'el',
  onAuthLost = null,
} = {}) {
  const base = baseUrl.replace(/\/$/, '');

  // A single in-flight refresh, shared by every request that hits a 401 at once.
  // Without this, a screen firing five parallel loads would burn five refresh
  // tokens and log the user out mid-event.
  let refreshInFlight = null;

  async function readTokens() {
    return {
      access: await tokenStore.get(ACCESS_KEY),
      refresh: await tokenStore.get(REFRESH_KEY),
    };
  }

  async function setTokens({ accessToken, refreshToken }) {
    if (accessToken) await tokenStore.set(ACCESS_KEY, accessToken);
    if (refreshToken) await tokenStore.set(REFRESH_KEY, refreshToken);
  }

  async function clearTokens() {
    await tokenStore.remove(ACCESS_KEY);
    await tokenStore.remove(REFRESH_KEY);
  }

  async function refreshAccessToken() {
    const { refresh } = await readTokens();
    if (!refresh) return null;

    const res = await fetch(`${base}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: refresh }),
    });

    if (!res.ok) {
      await clearTokens();
      return null;
    }
    const data = await res.json();
    await setTokens(data);
    return data.accessToken;
  }

  async function request(path, { method = 'GET', body, query, headers = {}, auth = true, _retried = false } = {}) {
    const url = new URL(`${base}${path}`, globalThis.location?.origin || 'http://localhost');
    if (query) {
      for (const [k, v] of Object.entries(query)) {
        if (v === undefined || v === null || v === '') continue;
        url.searchParams.set(k, Array.isArray(v) ? v.join(',') : String(v));
      }
    }

    const finalHeaders = {
      Accept: 'application/json',
      'Accept-Language': getLocale(),
      ...headers,
    };

    let payload = body;
    if (body !== undefined && !(body instanceof FormData)) {
      finalHeaders['Content-Type'] = 'application/json';
      payload = JSON.stringify(body);
    }

    if (auth) {
      const { access } = await readTokens();
      if (access) finalHeaders.Authorization = `Bearer ${access}`;
    }

    const res = await fetch(url.toString(), { method, headers: finalHeaders, body: payload });

    if (res.status === 401 && auth && !_retried) {
      const fresh = await (refreshInFlight ??= refreshAccessToken().finally(() => {
        refreshInFlight = null;
      }));
      if (fresh) {
        return request(path, { method, body, query, headers, auth, _retried: true });
      }
      await clearTokens();
      onAuthLost?.();
    }

    if (res.status === 204) return null;

    const text = await res.text();
    let data = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { message: text };
      }
    }

    if (!res.ok) {
      throw new ApiError(data?.message || data?.error || `Request failed (${res.status})`, {
        status: res.status,
        code: data?.code || data?.error,
        details: data?.details,
      });
    }

    return data;
  }

  /**
   * Base44-compatible entity wrapper.
   * `list(sort)` and `filter(where, sort)` keep the mockup's argument shapes.
   */
  function entity(name, pathSegment) {
    const root = `/${pathSegment}`;
    return {
      async list(sort) {
        const res = await request(root, { query: { sort } });
        return res?.data ?? res ?? [];
      },
      async filter(where = {}, sort) {
        const res = await request(root, { query: { ...where, sort } });
        return res?.data ?? res ?? [];
      },
      async get(id) {
        return request(`${root}/${encodeURIComponent(id)}`);
      },
      async create(payload) {
        return request(root, { method: 'POST', body: payload });
      },
      async update(id, payload) {
        return request(`${root}/${encodeURIComponent(id)}`, { method: 'PATCH', body: payload });
      },
      async delete(id) {
        return request(`${root}/${encodeURIComponent(id)}`, { method: 'DELETE' });
      },
      toString: () => `Entity(${name})`,
    };
  }

  return {
    request,
    setTokens,
    clearTokens,
    readTokens,

    auth: {
      /** Start an OTP login. Returns { sent: true } without revealing whether the email exists. */
      requestCode: (email) => request('/auth/request-code', { method: 'POST', body: { email }, auth: false }),
      /** Exchange the emailed code for tokens. */
      async verifyCode(email, code) {
        const data = await request('/auth/verify-code', { method: 'POST', body: { email, code }, auth: false });
        await setTokens(data);
        return data;
      },
      me: () => request('/auth/me'),
      async logout() {
        /*
         * Send the refresh token so the server can actually revoke it.
         *
         * Without it /auth/logout is a no-op that returns 204: it revokes only
         * what the body names, so signing out cleared the handset while the
         * refresh token stayed valid for its full 60 days. On borrowed
         * devices — which is how this event runs — that is the difference
         * between logging out and appearing to.
         */
        const { refresh } = await readTokens();
        try {
          await request('/auth/logout', {
            method: 'POST',
            body: refresh ? { refreshToken: refresh } : {},
          });
        } finally {
          // Always clear locally: a failed or unreachable revoke must still get
          // the previous person off this phone.
          await clearTokens();
        }
      },
    },

    participants: {
      /**
       * Register, and hold on to the session that comes back.
       *
       * The response carries a token pair as well as the participant, so the
       * attendee stays signed in afterwards and their QR is still there the
       * next time they open the app. Storing it here rather than at every call
       * site keeps the two clients from diverging on it.
       */
      async register(payload) {
        const data = await request('/participants/register', { method: 'POST', body: payload, auth: false });
        if (data?.accessToken) await setTokens(data);
        return data;
      },
      me: () => request('/participants/me'),
      updateMe: (payload) => request('/participants/me', { method: 'PATCH', body: payload }),
    },

    bookmarks: {
      list: () => request('/bookmarks'),
      add: (sessionId) => request('/bookmarks', { method: 'POST', body: { sessionId } }),
      remove: (sessionId) => request(`/bookmarks/${encodeURIComponent(sessionId)}`, { method: 'DELETE' }),
    },

    meetings: {
      slots: (representativeId) => request('/meetings/slots', { query: { representativeId } }),
      mine: () => request('/meetings/mine'),
      book: (representativeId, slotId) => request('/meetings', { method: 'POST', body: { representativeId, slotId } }),
      cancel: (id) => request(`/meetings/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    },

    checkIn: {
      /**
       * Idempotent by design: re-scanning the same ticket returns the original
       * check-in rather than erroring, because at a door queue that will happen.
       */
      scan: (qrToken, { deviceId, scannedAt } = {}) =>
        request('/check-ins', { method: 'POST', body: { qrToken, deviceId, scannedAt } }),
      /** Flush an offline queue in one round trip. */
      sync: (entries) => request('/check-ins/sync', { method: 'POST', body: { entries } }),
      stats: () => request('/check-ins/stats'),
    },

    notifications: {
      list: () => request('/notifications'),
      markRead: (id) => request(`/notifications/${encodeURIComponent(id)}/read`, { method: 'POST' }),
      markAllRead: () => request('/notifications/read-all', { method: 'POST' }),
      registerPushToken: (playerId) => request('/notifications/push-token', { method: 'POST', body: { playerId } }),
    },

    event: {
      settings: () => request('/event', { auth: false }),
      content: () => request('/content', { auth: false }),
      partners: () => request('/partners', { auth: false }),
    },

    account: {
      /*
       * Unauthenticated: the deletion page has to work for someone who can no
       * longer sign in, and Play requires the URL to be reachable without an
       * account. The reply never says whether the address is registered.
       */
      requestDeletion: (email, note) =>
        request('/account/deletion-request', { method: 'POST', body: { email, note }, auth: false }),

      /**
       * Erase the signed-in account, immediately. The tokens are cleared only
       * once the server confirms: if the call fails the account still exists,
       * and the person should still be signed in to it and able to retry.
       */
      async deleteSelf() {
        await request('/account', { method: 'DELETE' });
        await clearTokens();
      },
    },

    entities: {
      Session: entity('Session', 'sessions'),
      Speaker: entity('Speaker', 'speakers'),
      Participant: entity('Participant', 'participants'),
      Representative: entity('Representative', 'representatives'),
      Meeting: entity('Meeting', 'meetings'),
    },
  };
}

export default createClient;
